<script setup lang="ts">
const HIDDEN_UNTIL_KEY = "staff_mode_notice_hidden_until";
const CLOSE_DELAY_MS = 2_000;
const HIDE_MS = 10 * 60_000;

const STAFF_MESSAGE =
  "Staff mode: orders and shared carts placed from this browser are attributed to you.";
const CLOSING_MESSAGE = `Closing in ${CLOSE_DELAY_MS / 1000} sec, will be shown again in ${HIDE_MS / 60_000} min.`;

const authStore = useAuthStore();
const { track } = useTracking();

function readHiddenUntil(): number {
  try {
    const until = Number(sessionStorage.getItem(HIDDEN_UNTIL_KEY));
    return until > Date.now() ? until : 0;
  } catch {
    return 0;
  }
}

// The ✕ hides it for a while, not for good: long enough for a screenshot or a recording, and it
// still comes back while staff mode lasts. sessionStorage is per tab, so a new tab shows it at once.
const hiddenUntil = ref(readHiddenUntil());
const closing = ref(false);
let timer: ReturnType<typeof setTimeout> | undefined;

function schedule(fn: () => void, ms: number) {
  clearTimeout(timer);
  timer = setTimeout(fn, ms);
}

function setHiddenUntil(until: number) {
  hiddenUntil.value = until;
  try {
    if (until) sessionStorage.setItem(HIDDEN_UNTIL_KEY, String(until));
    else sessionStorage.removeItem(HIDDEN_UNTIL_KEY);
  } catch {
    /* blocked storage: the timer still brings it back on this page */
  }
}

function show() {
  setHiddenUntil(0);
}

function hide() {
  closing.value = false;
  setHiddenUntil(Date.now() + HIDE_MS);
  schedule(show, HIDE_MS);
}

function dismiss() {
  if (closing.value) return;
  closing.value = true;
  schedule(hide, CLOSE_DELAY_MS);
}

// A reload inside the 10 minutes stays hidden, and comes back when they run out.
onMounted(() => {
  if (hiddenUntil.value) schedule(show, hiddenUntil.value - Date.now());
});
onBeforeUnmount(() => clearTimeout(timer));

// Logging out keeps staff mode, so this is the way out, and it logs the customer out too.
// Un-hidden so the next staff session in this tab shows it.
function endStaffMode() {
  track("staff_mode_ended", {});
  authStore.endStaffMode();
  clearTimeout(timer);
  closing.value = false;
  show();
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="authStore.trackingId && !hiddenUntil"
      class="staff-mode-notice"
      role="status"
    >
      <BannerError
        variant="warning"
        :message="closing ? CLOSING_MESSAGE : STAFF_MESSAGE"
      />
      <DynamicButton
        class="staff-mode-notice__end"
        text="End staff mode"
        size="small"
        color="grey"
        @click-event="endStaffMode"
      />
      <button
        class="staff-mode-notice__close"
        aria-label="Dismiss staff mode notice"
        @click="dismiss"
      >
        <fa :icon="['fas', 'xmark']" />
      </button>
    </div>
  </Teleport>
</template>

<style lang="scss">
.staff-mode-notice {
  position: fixed;
  top: 5rem;
  left: var(--space-2);
  right: var(--space-2);
  // The fixed cart's layer, which the checkout breadcrumb also uses: teleported last, so it paints
  // over both where they overlap (the ✕ clears it), under the NavBar and the mobile cart overlay.
  z-index: $z-cart-fixed;
  box-sizing: border-box;
  padding-bottom: var(--space-2);
  // The box carries the warning look so the End button sits inside it.
  border-left: solid 2px var(--color-yellow-1);
  border-radius: 0.4rem;
  background-color: var(--color-yellow-2);

  @media (min-width: $tablet) {
    left: auto;
    max-width: 25rem;
  }

  .banner-error {
    box-sizing: border-box;
    width: 100%;
    margin: 0;
    padding-right: var(--tap);
    border-left: none;
    background-color: transparent;
  }

  &__end {
    margin-left: var(--space-2);
  }

  &__close {
    appearance: none;
    position: absolute;
    top: 0;
    right: 0;
    width: var(--tap);
    height: var(--tap);
    padding: 0;
    border: none;
    background: transparent;
    color: var(--color-grey-1);
    cursor: pointer;
  }
}
</style>
